import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Collapse,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { supabase } from "../../../components/SupabaseClient";
import MainPanelContent from "../components/MainPanelContent";
import {
  ACCENT,
  ACCENT_DARK,
  ACCENT_TINT,
  INK,
  INK_SECONDARY,
  INK_MUTED,
  HAIRLINE,
  SURFACE,
  SURFACE_MUTED,
  RADIUS,
  FONT,
  WEIGHT,
  STATUS,
  withAlpha,
} from "../../../theme/tokens";

const MONO =
  "'Fira Mono', 'Menlo', 'Monaco', 'Consolas', 'Liberation Mono', monospace";

const SSH_KEY_REGEX =
  /^(ssh-rsa|ecdsa-sha2-nistp256|ecdsa-sha2-nistp384|ecdsa-sha2-nistp521|ssh-ed25519)\s+([A-Za-z0-9+/]+(?:={0,2}))(?:\s+.*)?$/;

const KEY_TYPE_LABELS = {
  "ssh-ed25519": "ED25519",
  "ssh-rsa": "RSA",
  "ecdsa-sha2-nistp256": "ECDSA",
  "ecdsa-sha2-nistp384": "ECDSA",
  "ecdsa-sha2-nistp521": "ECDSA",
};

const parseKeyType = (keyString = "") => {
  const parts = keyString.trim().split(/\s+/);
  const algo = parts[0] || "";
  return {
    algo,
    label: KEY_TYPE_LABELS[algo] || "SSH",
    comment: parts.slice(2).join(" "),
  };
};

// Compute the OpenSSH-style SHA256 fingerprint (base64, no padding) from the
// base64 key blob — same format GitHub/GitLab display.
const computeFingerprint = async (keyString = "") => {
  const parts = keyString.trim().split(/\s+/);
  const b64 = parts[1];
  if (!b64 || !window.crypto?.subtle) return null;
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const digest = await window.crypto.subtle.digest("SHA-256", bytes);
    const hashB64 = btoa(
      String.fromCharCode(...new Uint8Array(digest)),
    ).replace(/=+$/, "");
    return `SHA256:${hashB64}`;
  } catch {
    return null;
  }
};

const useFingerprint = (keyString) => {
  const [fp, setFp] = useState(null);
  useEffect(() => {
    let active = true;
    computeFingerprint(keyString).then((v) => {
      if (active) setFp(v);
    });
    return () => {
      active = false;
    };
  }, [keyString]);
  return fp;
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const TypeBadge = ({ label }) => (
  <Box
    component="span"
    sx={{
      px: 0.875,
      py: 0.125,
      borderRadius: "999px",
      bgcolor: ACCENT_TINT,
      color: ACCENT_DARK,
      fontSize: FONT.xs,
      fontWeight: WEIGHT.semibold,
      letterSpacing: "0.02em",
      lineHeight: 1.6,
      flexShrink: 0,
    }}
  >
    {label}
  </Box>
);

const SshKeyRow = ({ keyData, onRequestDelete, deleting, disabled }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const { label, comment } = parseKeyType(keyData.key_string);
  const fingerprint = useFingerprint(keyData.key_string);

  const handleCopy = async () => {
    if (await copyToClipboard(keyData.key_string)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 2, p: 2 }}>
      {/* Key icon tile */}
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: `${RADIUS.sm}px`,
          bgcolor: withAlpha(ACCENT, 0.1),
          color: ACCENT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <VpnKeyOutlinedIcon sx={{ fontSize: 20 }} />
      </Box>

      {/* Identity + fingerprint */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{ fontSize: FONT.md, fontWeight: WEIGHT.semibold, color: INK }}
          >
            {keyData.key_name}
          </Typography>
          <TypeBadge label={label} />
        </Box>

        <Typography
          sx={{
            mt: 0.25,
            fontFamily: MONO,
            fontSize: FONT.xs,
            color: INK_MUTED,
            wordBreak: "break-all",
          }}
        >
          {fingerprint || "Fingerprint unavailable"}
        </Typography>

        {comment && (
          <Typography sx={{ mt: 0.25, fontSize: FONT.xs, color: INK_MUTED }}>
            {comment}
          </Typography>
        )}

        <Button
          variant="text"
          size="small"
          disableRipple
          onClick={() => setExpanded((v) => !v)}
          endIcon={
            expanded ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )
          }
          sx={{
            mt: 0.5,
            px: 0,
            minWidth: 0,
            color: INK_SECONDARY,
            fontSize: FONT.xs,
            fontWeight: WEIGHT.semibold,
            "&:hover": { bgcolor: "transparent", color: ACCENT_DARK },
          }}
        >
          {expanded ? "Hide public key" : "Show public key"}
        </Button>

        <Collapse in={expanded} unmountOnExit>
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              bgcolor: SURFACE_MUTED,
              border: `1px solid ${HAIRLINE}`,
              borderRadius: `${RADIUS.sm}px`,
              fontFamily: MONO,
              fontSize: FONT.xs,
              color: INK_SECONDARY,
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
              lineHeight: 1.6,
            }}
          >
            {keyData.key_string}
          </Box>
        </Collapse>
      </Box>

      {/* Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        <Tooltip title={copied ? "Copied!" : "Copy public key"}>
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{ color: INK_MUTED }}
          >
            {copied ? (
              <CheckIcon sx={{ fontSize: 18, color: STATUS.success }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: 17 }} />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete key">
          <span>
            <IconButton
              size="small"
              onClick={() => onRequestDelete(keyData)}
              disabled={deleting || disabled}
              sx={{
                color: INK_MUTED,
                "&:hover": {
                  color: STATUS.error,
                  bgcolor: withAlpha(STATUS.error, 0.08),
                },
              }}
            >
              {deleting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DeleteOutlineIcon sx={{ fontSize: 19 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

const MetadataView = ({
  user,
  sshKeys,
  sshKeyLoading,
  sshKeyLoadError,
  onSshKeyChange,
}) => {
  const [sshKeySubmitError, setSshKeySubmitError] = useState(null);
  const [sshKeyValidationError, setSshKeyValidationError] = useState("");
  const [showAddSshKeyInput, setShowAddSshKeyInput] = useState(false);
  const [newSshKeyName, setNewSshKeyName] = useState("");
  const [newSshKeyValue, setNewSshKeyValue] = useState("");
  const [isSubmittingNewKey, setIsSubmittingNewKey] = useState(false);
  const [deletingKeyName, setDeletingKeyName] = useState(null);
  const [keyToDelete, setKeyToDelete] = useState(null);

  const closeAddForm = () => {
    setShowAddSshKeyInput(false);
    setNewSshKeyName("");
    setNewSshKeyValue("");
    setSshKeySubmitError(null);
    setSshKeyValidationError("");
  };

  const isValidSshPublicKey = (keyString) => {
    if (!keyString) return true;
    return SSH_KEY_REGEX.test(keyString.trim());
  };

  const handleSaveNewSshKey = async () => {
    if (!user || !user.id || !newSshKeyName.trim() || !newSshKeyValue.trim()) {
      setSshKeySubmitError("Key name and value cannot be empty.");
      return;
    }
    if (!isValidSshPublicKey(newSshKeyValue)) {
      setSshKeyValidationError(
        "Invalid SSH public key format. Please check and try again.",
      );
      return;
    }
    setIsSubmittingNewKey(true);
    setSshKeySubmitError(null);
    setSshKeyValidationError("");

    try {
      const { error } = await supabase.rpc("create_user_public_key", {
        p_user_id: user.id,
        p_key_name: newSshKeyName.trim(),
        p_key_string: newSshKeyValue.trim(),
      });
      if (error) throw error;
      closeAddForm();
      onSshKeyChange();
    } catch (err) {
      console.error("Unexpected error creating SSH key:", err);
      setSshKeySubmitError(
        "An unexpected error occurred while adding the SSH key.",
      );
    } finally {
      setIsSubmittingNewKey(false);
    }
  };

  const handleConfirmDelete = async () => {
    const keyNameToDelete = keyToDelete?.key_name;
    setKeyToDelete(null);
    if (!user || !user.id || !keyNameToDelete) return;
    setDeletingKeyName(keyNameToDelete);
    setSshKeySubmitError(null);

    try {
      const { error } = await supabase.rpc("drop_user_public_key", {
        p_user_id: user.id,
        p_key_name: keyNameToDelete,
      });
      if (error) throw error;
      onSshKeyChange();
    } catch (err) {
      console.error(
        `Unexpected error deleting SSH key: ${keyNameToDelete}`,
        err,
      );
      setSshKeySubmitError(
        "An unexpected error occurred while deleting the SSH key.",
      );
    } finally {
      setDeletingKeyName(null);
    }
  };

  if (sshKeyLoading) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading SSH keys...</Typography>
      </Box>
    );
  }

  if (sshKeyLoadError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error fetching SSH keys: {sshKeyLoadError}
      </Alert>
    );
  }

  const hasKeys = sshKeys.length > 0;

  return (
    <MainPanelContent
      title="SSH Keys"
      rightNode={
        !showAddSshKeyInput ? (
          <Button
            variant="solidButton"
            startIcon={<AddIcon />}
            onClick={() => setShowAddSshKeyInput(true)}
            disabled={isSubmittingNewKey || !!deletingKeyName}
          >
            New SSH key
          </Button>
        ) : null
      }
    >
      <Box sx={{ width: "100%" }}>
        <Typography sx={{ fontSize: FONT.sm, color: INK_SECONDARY, mb: 2.5 }}>
          These public keys are used to authenticate when you connect to your
          workspace over SSH. You need at least one key to access a workspace.
        </Typography>

        {sshKeySubmitError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setSshKeySubmitError(null)}
          >
            {sshKeySubmitError}
          </Alert>
        )}

        {/* Add key form */}
        <Collapse in={showAddSshKeyInput} unmountOnExit>
          <Box
            component="form"
            noValidate
            autoComplete="off"
            sx={{
              mb: 3,
              p: 2.5,
              border: `1px solid ${HAIRLINE}`,
              borderRadius: `${RADIUS.md}px`,
              bgcolor: SURFACE,
            }}
          >
            <Typography
              sx={{
                fontSize: FONT.lg,
                fontWeight: WEIGHT.semibold,
                color: INK,
                mb: 2,
              }}
            >
              Add a new SSH key
            </Typography>

            <Typography
              sx={{
                fontSize: FONT.sm,
                fontWeight: WEIGHT.semibold,
                color: INK,
                mb: 0.75,
              }}
            >
              Title
            </Typography>
            <TextField
              placeholder="e.g. My Laptop"
              variant="outlined"
              fullWidth
              value={newSshKeyName}
              onChange={(e) => setNewSshKeyName(e.target.value)}
              disabled={isSubmittingNewKey}
              sx={{ mb: 2.5 }}
            />

            <Typography
              sx={{
                fontSize: FONT.sm,
                fontWeight: WEIGHT.semibold,
                color: INK,
                mb: 0.75,
              }}
            >
              Key
            </Typography>
            <TextField
              placeholder="Begins with ssh-rsa, ssh-ed25519, ecdsa-sha2-nistp256, …"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={newSshKeyValue}
              onChange={(e) => {
                const value = e.target.value;
                setNewSshKeyValue(value);
                if (!isValidSshPublicKey(value) && value.length > 0) {
                  setSshKeyValidationError(
                    "Invalid SSH public key format. Expected: 'key-type base64-key [comment]'",
                  );
                } else {
                  setSshKeyValidationError("");
                }
              }}
              error={!!sshKeyValidationError}
              helperText={
                sshKeyValidationError ||
                "Paste the contents of your public key file (usually ~/.ssh/id_ed25519.pub)."
              }
              disabled={isSubmittingNewKey}
              sx={{
                mb: 2,
                "& .MuiInputBase-input": {
                  fontFamily: MONO,
                  fontSize: "0.85rem",
                },
              }}
            />

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="solidButton"
                onClick={handleSaveNewSshKey}
                disabled={
                  isSubmittingNewKey ||
                  !newSshKeyName.trim() ||
                  !newSshKeyValue.trim() ||
                  !!sshKeyValidationError
                }
                startIcon={
                  isSubmittingNewKey ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                Add SSH key
              </Button>
              <Button
                variant="outlinedButton"
                onClick={closeAddForm}
                disabled={isSubmittingNewKey}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Collapse>

        {/* Keys list */}
        {hasKeys ? (
          <Box
            sx={{
              border: `1px solid ${HAIRLINE}`,
              borderRadius: `${RADIUS.md}px`,
              bgcolor: SURFACE,
              overflow: "hidden",
            }}
          >
            {sshKeys.map((key, index) => (
              <Box
                key={key.key_name || index}
                sx={{
                  borderTop: index === 0 ? "none" : `1px solid ${HAIRLINE}`,
                }}
              >
                <SshKeyRow
                  keyData={key}
                  onRequestDelete={setKeyToDelete}
                  deleting={deletingKeyName === key.key_name}
                  disabled={isSubmittingNewKey}
                />
              </Box>
            ))}
          </Box>
        ) : (
          !showAddSshKeyInput && (
            <Box
              sx={{
                border: `1px dashed ${HAIRLINE}`,
                borderRadius: `${RADIUS.md}px`,
                py: 6,
                px: 3,
                textAlign: "center",
              }}
            >
              <VpnKeyOutlinedIcon sx={{ fontSize: 40, color: INK_MUTED }} />
              <Typography
                sx={{
                  mt: 1,
                  fontSize: FONT.md,
                  fontWeight: WEIGHT.semibold,
                  color: INK,
                }}
              >
                No SSH keys yet
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: FONT.sm, color: INK_MUTED }}>
                Add a public key to connect to your workspaces over SSH.
              </Typography>
              <Button
                variant="solidButton"
                startIcon={<AddIcon />}
                onClick={() => setShowAddSshKeyInput(true)}
                sx={{ mt: 2.5 }}
              >
                New SSH key
              </Button>
            </Box>
          )
        )}
      </Box>

      {/* Delete confirmation */}
      <Dialog open={!!keyToDelete} onClose={() => setKeyToDelete(null)}>
        <DialogTitle>Delete SSH key</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{keyToDelete?.key_name}&quot;?
            Any workspace relying on this key for access will no longer be
            reachable with it. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlinedButton" onClick={() => setKeyToDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleConfirmDelete}
            sx={{ color: "error.main" }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </MainPanelContent>
  );
};

export default MetadataView;
